import { Button } from 'antd'
import type { ReactNode } from 'react'

import { notificationService } from '@/Core/Services/NotificationService'

import { NOTIFY_DURATION, NotifyQueue, buildNotifyKey, normalizeNotifyContent, type NotifyType } from './notifyContent'

export type { NotifyType }

export interface NotifyOptions {
    /** Другий рядок, якщо він справді щось додає до заголовка */
    description?: string
    /** Клік по повідомленню або кнопці дії */
    onClick?: () => void
    /** Підпис кнопки дії (коли є onClick) */
    actionLabel?: string
    /** Власний ключ: повідомлення з тим самим ключем оновлюється, а не додається */
    key?: string
    /** Перевизначити тривалість (секунди; false — до закриття) */
    duration?: number | false
}

const queue = new NotifyQueue()

/**
 * Єдиний спосіб показати системне повідомлення: завжди справа знизу, один формат.
 * Працює і в компонентах, і в сторах/задачах. Використовує екземпляр antd App ядра
 * (NotificationService), тож повідомлення ядра й проєкту стоять в одному стосі.
 *
 * Правила формату (див. notifyContent): «Успіх / Текст» → один рядок «Текст»;
 * успіх 4с, інфо й попередження 6с, помилка — до закриття; не більше 5 одночасно;
 * однакові повідомлення не дублюються.
 */
const show = (type: NotifyType, title?: ReactNode, options: NotifyOptions = {}): string | null => {
    const api = notificationService.getNotification()
    const content = normalizeNotifyContent(type, typeof title === 'string' ? title : '', options.description)
    if (!api) {
        console.warn('[notify] NotificationService ще не ініціалізовано:', content.title)
        return null
    }

    const key = options.key ?? buildNotifyKey(type, content.title, content.description)
    queue.push(key).forEach((oldKey) => api.destroy(oldKey))

    const duration = options.duration !== undefined ? options.duration : NOTIFY_DURATION[type]
    const close = () => api.destroy(key)
    const action = options.onClick
        ? () => {
              options.onClick?.()
              close()
          }
        : undefined

    api[type]({
        key,
        placement: 'bottomRight',
        className: `as-notify as-notify-${type}${action ? ' as-notify-clickable' : ''}`,
        title: typeof title === 'string' || title === undefined ? content.title : title,
        description: content.description,
        duration,
        showProgress: duration !== false,
        pauseOnHover: true,
        onClose: () => queue.remove(key),
        ...(action
            ? {
                  onClick: action,
                  actions: (
                      <Button
                          type="primary"
                          size="small"
                          onClick={(event) => {
                              event.stopPropagation()
                              action()
                          }}
                      >
                          {options.actionLabel ?? 'Перейти'}
                      </Button>
                  ),
              }
            : {}),
    })

    return key
}

export const notify = {
    success: (title?: ReactNode, options?: NotifyOptions) => show('success', title, options),
    error: (title?: ReactNode, options?: NotifyOptions) => show('error', title, options),
    info: (title?: ReactNode, options?: NotifyOptions) => show('info', title, options),
    warning: (title?: ReactNode, options?: NotifyOptions) => show('warning', title, options),
    /** Закрити одне повідомлення за ключем */
    close: (key: string) => {
        queue.remove(key)
        notificationService.getNotification()?.destroy(key)
    },
}
